import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Moon, Sun, Menu, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar: React.FC = () => {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.docs'), href: '/docs' },
    { name: t('nav.community'), href: 'https://community.h2chain.io/', external: true },
    { name: t('nav.wallet'), href: '/wallet' },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div className="w-full h-16 flex items-center relative bg-white dark:bg-[#111111] border-b border-gray-100 dark:border-gray-700 pointer-events-auto transition-colors duration-300">
          {/* Logo and Brand Name - 最左侧 */}
          <Link 
            to="/" 
            className="flex items-center gap-3 cursor-pointer pl-4 md:pl-6"
          >
            <img 
              src="/h2_logo.svg" 
              alt="H2Chain Logo" 
              className="w-8 h-8"
            />
            <span className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              H2Chain
            </span>
          </Link>

          {/* Navigation Links - 居中 (桌面端) */}
          <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 items-center gap-8">
            {navLinks.map((link) => {
              const linkClassName = `text-sm font-medium tracking-wide transition-colors duration-200 ${
                isActive(link.href)
                  ? 'text-[#86E92A] hover:text-[#75d320]'
                  : 'text-gray-400 hover:text-gray-300 dark:hover:text-gray-100'
              }`;

              return link.external ? (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClassName}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={linkClassName}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          {/* Right side controls */}
          <div className="ml-auto mr-4 md:mr-6 flex items-center gap-2">
            {/* Desktop: Language Switcher and Theme Toggle */}
            <div className="hidden md:flex items-center gap-2">
              <LanguageSwitcher />
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                aria-label="Toggle theme"
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                ) : (
                  <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                )}
              </button>
            </div>

            {/* Mobile: Hamburger Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden pointer-events-auto">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute top-16 left-0 right-0 bg-white dark:bg-[#111111] border-b border-gray-100 dark:border-gray-700 shadow-lg">
            <div className="px-4 py-6 space-y-4">
              {/* Navigation Links */}
              {navLinks.map((link) => {
                const linkClassName = `block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-200 ${
                  isActive(link.href)
                    ? 'bg-[#86E92A]/10 text-[#86E92A]'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`;

                return link.external ? (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClassName}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={linkClassName}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                );
              })}

              {/* Divider */}
              <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

              {/* Mobile: Language Switcher and Theme Toggle */}
              <div className="flex items-center justify-between px-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('nav.settings')}</span>
                <div className="flex items-center gap-2">
                  <LanguageSwitcher />
                  <button
                    onClick={toggleTheme}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                    aria-label="Toggle theme"
                  >
                    {theme === 'light' ? (
                      <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    ) : (
                      <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
